// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';

import { Card, InfoBanner, Th, Td } from '../../../components/WorkflowFormPrimitives';
import { fmt } from '../../../utils/formatCurrency';
import { useCompanyContext } from '../../../context/CompanyContext';
import { getSeverancePresentValueNoteBody } from '../parachuteCopy';
import { getUniqueIndividualsFromForm } from './uniqueIndividuals';
import { parseLocalDateFromIsoYmd, parseWorkflowDate } from '../../../../../utils/isoDateDisplay';

export const SeveranceContent = ({ formData, setFormData }) => {
  const { currentAnalysisId } = useCompanyContext();
  const uniqueIndividuals = getUniqueIndividualsFromForm(formData);
  const [data, setData] = useState(formData.severanceData || {});
  const lastIncomingSeveranceJson = useRef(null);

  useEffect(() => {
    lastIncomingSeveranceJson.current = null;
  }, [currentAnalysisId]);

  useEffect(() => {
    const incoming = formData.severanceData || {};
    const json = JSON.stringify(incoming);
    if (lastIncomingSeveranceJson.current !== json) {
      lastIncomingSeveranceJson.current = json;
      setData(incoming);
    }
  }, [formData.severanceData]);

  const update = (id, k, v) => setData((p) => ({ ...p, [id]: { ...p[id], [k]: v } }));

  const calcPV = (fv, months) => {
    if (months <= 0 || fv <= 0) return fv;
    const afr = (+formData.afr || 5) / 100;
    const discountRate = 1.2 * afr;
    const years = months / 12;
    const periods = years * 2;
    return fv / Math.pow(1 + discountRate / 2, periods);
  };

  const calcStreamPV = (monthlyAmount, numMonths) => {
    if (monthlyAmount <= 0 || numMonths <= 0) return 0;
    let totalPV = 0;
    for (let m = 0; m < numMonths; m++) {
      totalPV += calcPV(monthlyAmount, m);
    }
    return totalPV;
  };

  const calc = (ind) => {
    const d = data[ind.id] || {};
    const comp = formData.compensation?.[ind.id] || {};
    const baseSalary = +comp.baseSalary || 0;
    const bonusPct = +comp.bonusPct || 0;
    const targetBonus = baseSalary * bonusPct;
    const healthPremium = +comp.healthPremium || 1500;
    const salaryAmount = +d.salaryAmount || 0;
    const salaryUnit = d.salaryUnit || 'months';
    const cobraMonths = +d.cobraMonths || 0;
    const other = +d.other || 0;
    const paymentTiming = d.paymentTiming || 'lumpAtClose';
    const salaryMonths = salaryUnit === 'months' ? salaryAmount : salaryAmount / 4.33;
    let salaryAmt = salaryUnit === 'months' ? (baseSalary / 12) * salaryAmount : (baseSalary / 52) * salaryAmount;
    let bonusAmt = 0;
    if (d.bonusPortion === 'prorated') {
      const dt = parseLocalDateFromIsoYmd(formData.closingDate) || parseWorkflowDate(formData.closingDate);
      if (dt) {
        const days = Math.floor((dt.getTime() - new Date(dt.getFullYear(), 0, 1).getTime()) / 864e5);
        bonusAmt = (targetBonus * days) / 365;
      }
    } else if (d.bonusPortion === '50') bonusAmt = targetBonus * 0.5;
    else if (d.bonusPortion === '100') bonusAmt = targetBonus;
    const cobraAmt = healthPremium * cobraMonths;
    const nominalTotal = salaryAmt + bonusAmt + cobraAmt + other;
    let salaryPV = salaryAmt,
      bonusPV = bonusAmt,
      cobraPV = cobraAmt,
      otherPV = other;
    if (paymentTiming === 'paidOverTime') {
      const monthlySalary = baseSalary / 12;
      salaryPV = calcStreamPV(monthlySalary, salaryMonths);
      cobraPV = calcStreamPV(healthPremium, cobraMonths);
    }
    const pvTotal = salaryPV + bonusPV + cobraPV + otherPV;
    return {
      salaryAmt,
      bonusAmt,
      cobraAmt,
      other,
      salaryPV,
      bonusPV,
      cobraPV,
      otherPV,
      nominalTotal,
      pvTotal,
      hasPVDifference: Math.abs(nominalTotal - pvTotal) > 1,
    };
  };

  useEffect(() => {
    setFormData((p) => ({ ...p, severanceData: data }));
  }, [data]);

  return (
    <div className="space-y-4">
      <Card padding={false}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <Th>Name</Th>
                <Th>Salary Coverage</Th>
                <Th>Unit</Th>
                <Th>Bonus Portion</Th>
                <Th>COBRA (Mo)</Th>
                <Th>Other</Th>
                <Th>Payment Timing</Th>
                <Th className="bg-gray-100">Nominal</Th>
                <Th className="bg-emerald-50/50">Parachute value</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {uniqueIndividuals.map((ind) => {
                const c = calc(ind);
                const d = data[ind.id] || {};
                return (
                  <tr key={ind.id} className="hover:bg-gray-50/50">
                    <Td highlight>
                      <div>
                        <span className="text-gray-900">{ind.name}</span>
                        <br />
                        <span className="text-xs text-gray-400">{ind.title}</span>
                      </div>
                    </Td>
                    <Td>
                      <input
                        type="number"
                        value={d.salaryAmount || ''}
                        onChange={(e) => update(ind.id, 'salaryAmount', e.target.value)}
                        className="w-16 h-8 px-2 text-xs border border-gray-300 rounded-md"
                        placeholder="0"
                      />
                    </Td>
                    <Td>
                      <select
                        value={d.salaryUnit || 'months'}
                        onChange={(e) => update(ind.id, 'salaryUnit', e.target.value)}
                        className="h-8 px-2 text-xs border border-gray-300 rounded-md"
                      >
                        <option value="months">Mo</option>
                        <option value="weeks">Wk</option>
                      </select>
                    </Td>
                    <Td>
                      <select
                        value={d.bonusPortion || 'none'}
                        onChange={(e) => update(ind.id, 'bonusPortion', e.target.value)}
                        className="h-8 px-2 text-xs border border-gray-300 rounded-md"
                      >
                        <option value="none">None</option>
                        <option value="prorated">Pro Rata</option>
                        <option value="50">50%</option>
                        <option value="100">100%</option>
                      </select>
                    </Td>
                    <Td>
                      <input
                        type="number"
                        value={d.cobraMonths || ''}
                        onChange={(e) => update(ind.id, 'cobraMonths', e.target.value)}
                        className="w-14 h-8 px-2 text-xs border border-gray-300 rounded-md"
                        placeholder="0"
                      />
                    </Td>
                    <Td>
                      <input
                        type="number"
                        value={d.other || ''}
                        onChange={(e) => update(ind.id, 'other', e.target.value)}
                        className="w-20 h-8 px-2 text-xs border border-gray-300 rounded-md"
                        placeholder="0"
                      />
                    </Td>
                    <Td>
                      <select
                        value={d.paymentTiming || 'lumpAtClose'}
                        onChange={(e) => update(ind.id, 'paymentTiming', e.target.value)}
                        className="h-8 px-2 text-xs border border-gray-300 rounded-md"
                      >
                        <option value="lumpAtClose">Lump sum at close</option>
                        <option value="paidOverTime">Paid over time</option>
                      </select>
                    </Td>
                    <Td className="bg-gray-50 text-gray-500">{fmt(c.nominalTotal)}</Td>
                    <Td className="bg-emerald-50/30 text-emerald-700">
                      <span className="font-bold">{fmt(c.pvTotal)}</span>
                      {c.hasPVDifference && (
                        <span className="text-xs text-gray-400 block font-normal">
                          ({fmt(c.nominalTotal - c.pvTotal)} discount)
                        </span>
                      )}
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
      <InfoBanner showIcon>
        <strong>Present Value:</strong> {getSeverancePresentValueNoteBody(formData)}
      </InfoBanner>
    </div>
  );
};
